import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  BarChart3,
  CalendarDays,
  ChevronDown,
  ChevronsUpDown,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  Loader2,
  Mail,
  RotateCcw,
  Save,
  Search,
  Settings,
  Users,
  X
} from 'lucide-react';
import api from '../../utils/api';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar/sidebar';
import './dailySummary.css';
import { API_BASE_URL } from '../../utils/apiBase';

const API_BASE = `${API_BASE_URL}/api/daily-summary`;

const activityOptions = [
  { value: 'prospects', label: 'Prospects Added' },
  { value: 'followUps', label: 'Follow-ups' },
  { value: 'policies', label: 'Policies Added' },
  { value: 'renewals', label: 'Renewals' },
  { value: 'claims', label: 'Claims' },
  { value: 'pending', label: 'Pending Tasks' }
];

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
  { value: 'policy_doc_uploaded', label: 'Policy Uploaded' },
  { value: 'sent_to_client', label: 'Sent to Client' },
  { value: 'Pending', label: 'Renewal Pending' },
  { value: 'In Progress', label: 'Renewal In Progress' },
  { value: 'Renewed', label: 'Renewed' },
  { value: 'Intimated', label: 'Claim Intimated' },
  { value: 'Surveyor Assigned', label: 'Surveyor Assigned' },
  { value: 'Settled', label: 'Claim Settled' }
];

const pageSizes = [10, 25, 50];

const defaultSettings = {
  enabled: false,
  scheduledTime: '18:00',
  reportFormat: 'excel',
  additionalRecipients: [],
  selectedUsers: [],
  dataOptions: {
    prospects: true,
    policies: true,
    renewals: true,
    claims: true,
    followUps: true,
    pending: true,
    premium: true
  }
};

const dataOptionLabels = [
  { key: 'prospects', label: 'Prospects Added' },
  { key: 'policies', label: 'Policies Added' },
  { key: 'renewals', label: 'Renewals Processed' },
  { key: 'claims', label: 'Claims Processed' },
  { key: 'followUps', label: 'Follow-ups Done' },
  { key: 'pending', label: 'Pending Tasks' },
  { key: 'premium', label: 'Total Premium Summary' }
];

const today = () => new Date().toISOString().slice(0, 10);

const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  });

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const sortValue = (row, key) => {
  if (key === 'date') return new Date(row.date || 0).getTime();
  if (key === 'premium') return Number(row.premium || 0);
  return String(row[key] || '').toLowerCase();
};

const DailySummary = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  const [status, setStatus] = useState('');
  const [activities, setActivities] = useState(activityOptions.map((item) => item.value));
  const [report, setReport] = useState({ summary: [], consolidated: {}, details: [] });
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [detailUserId, setDetailUserId] = useState('all');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSending, setSettingsSending] = useState(false);
  const [autoSettings, setAutoSettings] = useState(defaultSettings);
  const [settingsUserSearch, setSettingsUserSearch] = useState('');
  const [recipientText, setRecipientText] = useState('');
  const [emailLogs, setEmailLogs] = useState([]);
  const userDropdownRef = useRef(null);
  const availableUserIdSet = useMemo(() => new Set(users.map((user) => user.id)), [users]);
  const scopedSelectedUsers = useMemo(
    () => selectedUsers.filter((id) => availableUserIdSet.has(id)),
    [selectedUsers, availableUserIdSet]
  );

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUsers = async () => {
    setLoadingUsers(true);
    setError('');
    try {
      const res = await api.get(`${API_BASE}/users`);
      const rows = res.data || [];
      setUsers(rows);
      setSelectedUsers(rows.map((user) => user.id));
    } catch (err) {
      console.error('Daily summary users error:', err.response?.data || err);
      setError(err.response?.data?.error || 'Failed to load reporting users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const generateReport = async () => {
    if (!startDate || !endDate) {
      setError('Select a valid date or date range.');
      return;
    }

    if (scopedSelectedUsers.length === 0) {
      setReport({ summary: [], consolidated: {}, details: [] });
      setPage(1);
      return;
    }

    setLoadingReport(true);
    setError('');
    try {
      const res = await api.get(`${API_BASE}/report`, {
        params: {
          startDate,
          endDate,
          userIds: scopedSelectedUsers.join(','),
          status,
          activityTypes: activities.join(',')
        }
      });
      setReport(res.data || { summary: [], consolidated: {}, details: [] });
      setPage(1);
    } catch (err) {
      console.error('Daily summary report error:', err.response?.data || err);
      setError(err.response?.data?.error || 'Failed to generate daily summary');
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    if (selectedUsers.length) {
      generateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsers.length]);

  const toggleActivity = (value) => {
    setActivities((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  const toggleUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    setSelectedUsers(users.map((user) => user.id));
  };

  const deselectAllUsers = () => {
    setSelectedUsers([]);
  };

  const resetFilters = () => {
    setStartDate(today());
    setEndDate(today());
    setStatus('');
    setActivities(activityOptions.map((item) => item.value));
    setSelectedUsers(users.map((user) => user.id));
    setSearch('');
    setDetailUserId('all');
  };

  const loadSettings = async () => {
    setSettingsLoading(true);
    setError('');
    try {
      const [settingsRes, logsRes] = await Promise.all([
        api.get(`${API_BASE}/settings`),
        api.get(`${API_BASE}/email-logs`)
      ]);
      const nextSettings = {
        ...defaultSettings,
        ...(settingsRes.data || {}),
        selectedUsers: (settingsRes.data?.selectedUsers || []).filter((id) => availableUserIdSet.has(id)),
        dataOptions: {
          ...defaultSettings.dataOptions,
          ...(settingsRes.data?.dataOptions || {})
        }
      };
      setAutoSettings(nextSettings);
      setRecipientText((nextSettings.additionalRecipients || []).join(', '));
      setEmailLogs(logsRes.data || []);
      setSettingsOpen(true);
    } catch (err) {
      console.error('Daily summary settings load error:', err.response?.data || err);
      setError(err.response?.data?.error || 'Failed to load daily summary settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSettings = async () => {
    setSettingsSaving(true);
    setError('');
    try {
      const payload = {
        ...autoSettings,
        selectedUsers: (autoSettings.selectedUsers || []).filter((id) => availableUserIdSet.has(id)),
        additionalRecipients: recipientText
          .split(',')
          .map((email) => email.trim())
          .filter(Boolean)
      };
      const res = await api.put(`${API_BASE}/settings`, payload);
      const nextSettings = {
        ...defaultSettings,
        ...(res.data || {}),
        selectedUsers: (res.data?.selectedUsers || []).filter((id) => availableUserIdSet.has(id)),
        dataOptions: {
          ...defaultSettings.dataOptions,
          ...(res.data?.dataOptions || {})
        }
      };
      setAutoSettings(nextSettings);
      setRecipientText((nextSettings.additionalRecipients || []).join(', '));
    } catch (err) {
      console.error('Daily summary settings save error:', err.response?.data || err);
      setError(err.response?.data?.error || 'Failed to save daily summary settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const sendNow = async () => {
    setSettingsSending(true);
    setError('');
    try {
      await saveSettings();
      await api.post(`${API_BASE}/send-now`, { reportDate: today() });
      const logsRes = await api.get(`${API_BASE}/email-logs`);
      setEmailLogs(logsRes.data || []);
    } catch (err) {
      console.error('Daily summary send now error:', err.response?.data || err);
      setError(err.response?.data?.error || 'Failed to send daily summary');
    } finally {
      setSettingsSending(false);
    }
  };

  const resetSettings = () => {
    setAutoSettings({
      ...defaultSettings,
      selectedUsers: users.map((user) => user.id)
    });
    setRecipientText('');
    setSettingsUserSearch('');
  };

  const toggleSettingsUser = (userId) => {
    setAutoSettings((prev) => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(userId)
        ? prev.selectedUsers.filter((id) => id !== userId)
        : [...prev.selectedUsers, userId]
    }));
  };

  const toggleSettingsDataOption = (key) => {
    setAutoSettings((prev) => ({
      ...prev,
      dataOptions: {
        ...prev.dataOptions,
        [key]: !prev.dataOptions[key]
      }
    }));
  };

  const selectedUserNames = useMemo(() => {
    if (scopedSelectedUsers.length === users.length) return 'All available users';
    return users
      .filter((user) => scopedSelectedUsers.includes(user.id))
      .map((user) => user.name)
      .join(', ');
  }, [scopedSelectedUsers, users]);

  const selectedUserRows = useMemo(
    () => users.filter((user) => scopedSelectedUsers.includes(user.id)),
    [scopedSelectedUsers, users]
  );

  const filteredUsers = useMemo(() => {
    const needle = userSearch.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((user) =>
      [user.name, user.employeeId, user.username, user.email]
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [users, userSearch]);

  const userDropdownLabel = useMemo(() => {
    if (loadingUsers) return 'Loading users...';
    if (!scopedSelectedUsers.length) return 'No users selected';
    if (scopedSelectedUsers.length === users.length) return 'All users selected';
    if (scopedSelectedUsers.length <= 2) {
      return selectedUserRows.map((user) => user.name).join(', ');
    }
    return `${scopedSelectedUsers.length} Users Selected`;
  }, [loadingUsers, scopedSelectedUsers, selectedUserRows, users.length]);

  const settingsFilteredUsers = useMemo(() => {
    const needle = settingsUserSearch.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((user) =>
      [user.name, user.employeeId, user.username, user.email]
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [users, settingsUserSearch]);

  const filteredDetails = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (report.details || [])
      .filter((row) => detailUserId === 'all' || row.userId === detailUserId)
      .filter((row) => {
        if (!needle) return true;
        return [
          row.userName,
          row.activityType,
          row.status,
          row.reference,
          row.clientName,
          row.policyName,
          row.notes
        ]
          .join(' ')
          .toLowerCase()
          .includes(needle);
      })
      .sort((a, b) => {
        const aValue = sortValue(a, sortConfig.key);
        const bValue = sortValue(b, sortConfig.key);
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
  }, [report.details, search, sortConfig, detailUserId]);

  const totalPages = Math.max(1, Math.ceil(filteredDetails.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedDetails = filteredDetails.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const setSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const exportExcel = () => {
    const summarySheet = XLSX.utils.json_to_sheet(
      (report.summary || []).map((row) => ({
        User: row.userName,
        'Employee ID': row.employeeId,
        'Prospects Added': row.prospectsAdded,
        'Follow-ups Done': row.followUpsDone,
        'Policies Added': row.policiesAdded,
        'Renewals Processed': row.renewalsProcessed,
        'Claims Processed': row.claimsProcessed,
        'Total Premium': row.totalPremium,
        'Pending Tasks': row.pendingTasks
      }))
    );

    const detailsSheet = XLSX.utils.json_to_sheet(
      filteredDetails.map((row) => ({
        Date: formatDate(row.date),
        User: row.userName,
        Activity: row.activityType,
        Status: row.status,
        Reference: row.reference,
        Client: row.clientName,
        Policy: row.policyName,
        Amount: row.premium,
        Notes: row.notes
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'User Summary');
    XLSX.utils.book_append_sheet(workbook, detailsSheet, 'Drilldown');
    XLSX.writeFile(workbook, `daily-summary-${startDate}-to-${endDate}.xlsx`);
  };

  const exportPdf = () => {
    window.print();
  };

  const cards = [
    { label: 'Prospects Added', value: report.consolidated?.prospectsAdded, tone: 'blue' },
    { label: 'Follow-ups Done', value: report.consolidated?.followUpsDone, tone: 'green' },
    { label: 'Policies Added', value: report.consolidated?.policiesAdded, tone: 'orange' },
    { label: 'Renewals Processed', value: report.consolidated?.renewalsProcessed, tone: 'violet' },
    { label: 'Claims Processed', value: report.consolidated?.claimsProcessed, tone: 'red' },
    { label: 'Pending Tasks', value: report.consolidated?.pendingTasks, tone: 'amber' },
    { label: 'Total Premium', value: formatCurrency(report.consolidated?.totalPremium), tone: 'teal', wide: true }
  ];

  return (
    <div className="daily-summary-container">
      <div className="sidebar">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>
      <div className={`daily-summary-content ${collapsed ? 'collapsed' : ''}`}>
        <div className="daily-summary-sticky-header">
          <Header />
        </div>

        <main className="daily-summary-scroll-area">
          <section className="daily-summary-shell">
            <div className="daily-summary-title-row">
              <div>
                <span className="daily-summary-eyebrow">
                  <BarChart3 size={16} /> Reports
                </span>
                <h2>Daily Summary</h2>
                <p>{selectedUserNames || 'Select users from your reporting hierarchy'}</p>
              </div>
              <div className="daily-summary-actions">
                <button type="button" onClick={loadSettings} disabled={settingsLoading}>
                  {settingsLoading ? <Loader2 size={16} className="spin" /> : <Settings size={16} />} Settings
                </button>
                <button type="button" onClick={exportPdf} disabled={loadingReport}>
                  <FileText size={16} /> PDF
                </button>
                <button type="button" onClick={exportExcel} disabled={loadingReport}>
                  <FileSpreadsheet size={16} /> Excel
                </button>
              </div>
            </div>

            {error && <div className="daily-summary-alert">{error}</div>}

            <section className="daily-summary-filters">
              <label>
                <span><CalendarDays size={15} /> From</span>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </label>
              <label>
                <span><CalendarDays size={15} /> To</span>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </label>
              <label>
                <span><Filter size={15} /> Status</span>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <div className="daily-summary-users" ref={userDropdownRef}>
                <span><Users size={15} /> Users</span>
                <button
                  type="button"
                  className={`daily-summary-user-trigger ${userDropdownOpen ? 'open' : ''}`}
                  onClick={() => setUserDropdownOpen((open) => !open)}
                  disabled={loadingUsers}
                  aria-haspopup="listbox"
                  aria-expanded={userDropdownOpen}
                >
                  <div className="daily-summary-user-trigger-content">
                    {selectedUserRows.length > 0 && selectedUserRows.length <= 2 ? (
                      <div className="daily-summary-user-chips">
                        {selectedUserRows.map((user) => (
                          <span key={user.id} className="daily-summary-user-chip">
                            {user.name}
                            {user.employeeId ? <small>{user.employeeId}</small> : null}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <strong>{userDropdownLabel}</strong>
                    )}
                    <small>{scopedSelectedUsers.length} of {users.length} available</small>
                  </div>
                  <ChevronDown size={18} />
                </button>

                {userDropdownOpen && (
                  <div className="daily-summary-user-menu" role="listbox" aria-multiselectable="true">
                    <div className="daily-summary-user-menu-toolbar">
                      <label className="daily-summary-user-search">
                        <Search size={15} />
                        <input
                          type="search"
                          placeholder="Search user or employee ID"
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          autoFocus
                        />
                        {userSearch && (
                          <button type="button" onClick={() => setUserSearch('')} aria-label="Clear user search">
                            <X size={14} />
                          </button>
                        )}
                      </label>
                      <div className="daily-summary-user-menu-actions">
                        <button type="button" onClick={selectAllUsers}>Select All</button>
                        <button type="button" onClick={deselectAllUsers}>Deselect All</button>
                      </div>
                    </div>

                    <div className="daily-summary-user-options">
                      {filteredUsers.map((user) => (
                        <label key={user.id} className="daily-summary-user-option">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => toggleUser(user.id)}
                          />
                          <span className="daily-summary-checkbox-ui" aria-hidden="true" />
                          <span className="daily-summary-user-option-text">
                            <strong>{user.name}</strong>
                            <small>{user.employeeId || 'No Employee ID'}</small>
                          </span>
                        </label>
                      ))}
                      {filteredUsers.length === 0 && (
                        <div className="daily-summary-user-empty">No users match your search.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="daily-summary-activity-filter">
                <span>Activity Types</span>
                <div>
                  {activityOptions.map((option) => (
                    <label key={option.value}>
                      <input
                        type="checkbox"
                        checked={activities.includes(option.value)}
                        onChange={() => toggleActivity(option.value)}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="daily-summary-filter-actions">
                <button type="button" className="secondary" onClick={resetFilters}>
                  <RotateCcw size={16} /> Reset
                </button>
                <button type="button" onClick={generateReport} disabled={loadingReport || loadingUsers}>
                  {loadingReport ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
                  Generate
                </button>
              </div>
            </section>

            <section className="daily-summary-card-grid">
              {cards.map((card) => (
                <article key={card.label} className={`daily-summary-card ${card.tone} ${card.wide ? 'wide' : ''}`}>
                  <span>{card.label}</span>
                  <strong>{typeof card.value === 'string' ? card.value : formatNumber(card.value)}</strong>
                </article>
              ))}
            </section>

            <section className="daily-summary-panel">
              <div className="daily-summary-panel-header">
                <div>
                  <h3>User-wise Summary</h3>
                  <p>Click a row to drill into that user's activity records.</p>
                </div>
              </div>
              <div className="daily-summary-table-wrap compact">
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Prospects</th>
                      <th>Follow-ups</th>
                      <th>Policies</th>
                      <th>Renewals</th>
                      <th>Claims</th>
                      <th>Premium</th>
                      <th>Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.summary || []).map((row) => (
                      <tr
                        key={row.userId}
                        className={detailUserId === row.userId ? 'selected' : ''}
                        onClick={() => {
                          setDetailUserId(row.userId);
                          setPage(1);
                        }}
                      >
                        <td>
                          <strong>{row.userName}</strong>
                          <small>{row.employeeId || '-'}</small>
                        </td>
                        <td>{formatNumber(row.prospectsAdded)}</td>
                        <td>{formatNumber(row.followUpsDone)}</td>
                        <td>{formatNumber(row.policiesAdded)}</td>
                        <td>{formatNumber(row.renewalsProcessed)}</td>
                        <td>{formatNumber(row.claimsProcessed)}</td>
                        <td>{formatCurrency(row.totalPremium)}</td>
                        <td>{formatNumber(row.pendingTasks)}</td>
                      </tr>
                    ))}
                    {!loadingReport && (report.summary || []).length === 0 && (
                      <tr>
                        <td colSpan="8" className="empty">No summary data found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="daily-summary-panel">
              <div className="daily-summary-panel-header">
                <div>
                  <h3>Drill-down Records</h3>
                  <p>{filteredDetails.length} records in the current view.</p>
                </div>
                <div className="daily-summary-detail-tools">
                  <label className="daily-summary-search">
                    <Search size={16} />
                    <input
                      type="search"
                      value={search}
                      placeholder="Search activity, client, policy..."
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                  </label>
                  <select
                    value={detailUserId}
                    onChange={(e) => {
                      setDetailUserId(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="all">All users</option>
                    {(report.summary || []).map((row) => (
                      <option key={row.userId} value={row.userId}>{row.userName}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="daily-summary-table-wrap">
                <table>
                  <thead>
                    <tr>
                      {[
                        ['date', 'Date'],
                        ['userName', 'User'],
                        ['activityType', 'Activity'],
                        ['status', 'Status'],
                        ['reference', 'Reference'],
                        ['clientName', 'Client'],
                        ['policyName', 'Policy'],
                        ['premium', 'Amount']
                      ].map(([key, label]) => (
                        <th key={key} onClick={() => setSort(key)}>
                          {label} <ChevronsUpDown size={13} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDetails.map((row) => (
                      <tr key={row.id}>
                        <td>{formatDate(row.date)}</td>
                        <td>{row.userName}</td>
                        <td><span className="daily-summary-badge">{row.activityType}</span></td>
                        <td>{row.status || '-'}</td>
                        <td>{row.reference || '-'}</td>
                        <td>{row.clientName || '-'}</td>
                        <td>{row.policyName || '-'}</td>
                        <td>{formatCurrency(row.premium)}</td>
                      </tr>
                    ))}
                    {!loadingReport && paginatedDetails.length === 0 && (
                      <tr>
                        <td colSpan="8" className="empty">No activity records match the current filters.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="daily-summary-pagination">
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <select value={pageSize} onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}>
                  {pageSizes.map((size) => (
                    <option key={size} value={size}>{size} rows</option>
                  ))}
                </select>
                <button type="button" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={currentPage === 1}>
                  <ChevronDown size={16} className="prev-icon" /> Previous
                </button>
                <button type="button" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>
                  Next <ChevronDown size={16} className="next-icon" />
                </button>
              </div>
            </section>
          </section>
        </main>
      </div>

      {settingsOpen && (
        <div className="daily-summary-modal-backdrop" role="presentation">
          <section className="daily-summary-settings-modal" role="dialog" aria-modal="true" aria-labelledby="daily-summary-settings-title">
            <header className="daily-summary-settings-header">
              <div>
                <h3 id="daily-summary-settings-title">Daily Summary Settings</h3>
                <p>Automate daily report delivery to the reporting manager.</p>
              </div>
              <button type="button" onClick={() => setSettingsOpen(false)} aria-label="Close settings">
                <X size={18} />
              </button>
            </header>

            <div className="daily-summary-settings-body">
              <section className="daily-summary-settings-section">
                <div className="daily-summary-settings-section-title">
                  <h4>Schedule</h4>
                  <label className="daily-summary-toggle">
                    <input
                      type="checkbox"
                      checked={autoSettings.enabled}
                      onChange={(e) => setAutoSettings((prev) => ({ ...prev, enabled: e.target.checked }))}
                    />
                    <span />
                    Enable Auto Daily Report
                  </label>
                </div>
                <div className="daily-summary-settings-grid">
                  <label>
                    <span>Report Sending Time</span>
                    <input
                      type="time"
                      value={autoSettings.scheduledTime}
                      onChange={(e) => setAutoSettings((prev) => ({ ...prev, scheduledTime: e.target.value }))}
                    />
                  </label>
                  <label>
                    <span>Report Format</span>
                    <select
                      value={autoSettings.reportFormat}
                      onChange={(e) => setAutoSettings((prev) => ({ ...prev, reportFormat: e.target.value }))}
                    >
                      <option value="pdf">PDF</option>
                      <option value="excel">Excel</option>
                      <option value="both">Both</option>
                    </select>
                  </label>
                </div>
              </section>

              <section className="daily-summary-settings-section">
                <h4>Recipients</h4>
                <div className="daily-summary-settings-grid">
                  <label>
                    <span>Default Recipient</span>
                    <input type="text" value="Reporting Manager" disabled />
                  </label>
                  <label>
                    <span>Additional Email Recipients</span>
                    <input
                      type="text"
                      value={recipientText}
                      onChange={(e) => setRecipientText(e.target.value)}
                      placeholder="name@example.com, team@example.com"
                    />
                  </label>
                </div>
              </section>

              <section className="daily-summary-settings-section">
                <div className="daily-summary-settings-section-title">
                  <h4>Included Users</h4>
                  <div className="daily-summary-settings-inline-actions">
                    <button type="button" onClick={() => setAutoSettings((prev) => ({ ...prev, selectedUsers: users.map((user) => user.id) }))}>
                      Select All
                    </button>
                    <button type="button" onClick={() => setAutoSettings((prev) => ({ ...prev, selectedUsers: [] }))}>
                      Deselect All
                    </button>
                  </div>
                </div>
                <label className="daily-summary-settings-search">
                  <Search size={15} />
                  <input
                    type="search"
                    value={settingsUserSearch}
                    onChange={(e) => setSettingsUserSearch(e.target.value)}
                    placeholder="Search users"
                  />
                </label>
                <div className="daily-summary-settings-user-list">
                  {settingsFilteredUsers.map((user) => (
                    <label key={user.id} className="daily-summary-settings-user-row">
                      <input
                        type="checkbox"
                        checked={autoSettings.selectedUsers.includes(user.id)}
                        onChange={() => toggleSettingsUser(user.id)}
                      />
                      <span>{user.name}</span>
                      <small>{user.employeeId || 'No Employee ID'}</small>
                    </label>
                  ))}
                </div>
              </section>

              <section className="daily-summary-settings-section">
                <h4>Report Data Options</h4>
                <div className="daily-summary-settings-options">
                  {dataOptionLabels.map((option) => (
                    <label key={option.key}>
                      <input
                        type="checkbox"
                        checked={autoSettings.dataOptions[option.key]}
                        onChange={() => toggleSettingsDataOption(option.key)}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </section>

              <section className="daily-summary-settings-section">
                <h4>Email Logs</h4>
                <div className="daily-summary-email-log-list">
                  {emailLogs.slice(0, 5).map((log) => (
                    <div key={log._id} className={`daily-summary-email-log ${log.status === 'Success' ? 'success' : 'failed'}`}>
                      <strong>{log.status}</strong>
                      <span>{formatDate(log.sentAt || log.createdAt)} · {log.recipients?.join(', ') || '-'}</span>
                    </div>
                  ))}
                  {emailLogs.length === 0 && <p>No email history yet.</p>}
                </div>
              </section>
            </div>

            <footer className="daily-summary-settings-footer">
              <button type="button" className="secondary" onClick={resetSettings}>
                <RotateCcw size={16} /> Reset
              </button>
              <button type="button" className="secondary" onClick={sendNow} disabled={settingsSaving || settingsSending}>
                {settingsSending ? <Loader2 size={16} className="spin" /> : <Mail size={16} />} Send Now
              </button>
              <button type="button" onClick={saveSettings} disabled={settingsSaving}>
                {settingsSaving ? <Loader2 size={16} className="spin" /> : <Save size={16} />} Save
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
};

export default DailySummary;
